// Copyright (c) Bentley Systems
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SelectOption } from "@itwin/itwinui-react";
import { AppDispatch } from "../app/store";
import { loadingSkeleton, searchEmptyMessage } from "../Helpers/ComboBoxHelpers";
import { ConfigManager } from "../Helpers/config-manager";
import { useDebouncedFetch } from "../Helpers/useDebouncedFetch";
import { setErrorState } from "../Helpers/helper-functions";
import { mapDataToModels } from "../Helpers/map-response";
import { CatalogGeneralInfo } from "../ProjectManagement/Models/catalog-general-info";
import { ProjectApiHelper } from "../ProjectManagement/project-api-helper";

/**
 * Hook for the catalog project ComboBox in ApiTesting.
 *
 * Given an iTwinId, fetches subscribed catalog projects (OData, debounced)
 * and exposes ComboBox-ready select options + an input handler.
 *
 * When the user selects a project, fetches its full details to populate
 * the request body with the project's InputExample.
 */
type UseCatalogProjectsProps = {
  iTwinId: string | undefined;
  accessToken: string;
  dispatch: AppDispatch;
};

export const useCatalogProjects = ({
  iTwinId,
  accessToken,
  dispatch,
}: UseCatalogProjectsProps) => {
  const [catalogProjects, setCatalogProjects] = useState<CatalogGeneralInfo[]>([]);
  const [selectedCatalogProject, setSelectedCatalogProject] = useState<CatalogGeneralInfo | null>(null);
  const [isCatalogProjectsLoading, setIsCatalogProjectsLoading] = useState(false);
  const [catalogSearchString, setCatalogSearchString] = useState<string | undefined>(undefined);
  const [isCatalogSearchActive, setIsCatalogSearchActive] = useState(false);
  const [catalogRawInput, setCatalogRawInput] = useState("");
  const [catalogTotalCount, setCatalogTotalCount] = useState(0);

  // Request body state (populated from selected project's InputExample)
  const [requestBody, setRequestBody] = useState("{}");
  const [requestBodyKey, setRequestBodyKey] = useState(0);

  // Keep accessToken in a ref so token refreshes don't recreate fetch callbacks
  // and trigger debounced re-fetches (which would reload the list unnecessarily).
  const accessTokenRef = useRef(accessToken);
  useEffect(() => { accessTokenRef.current = accessToken; }, [accessToken]);

  const buildCatalogFilter = useCallback(() => {
    if (isCatalogSearchActive && catalogSearchString && catalogSearchString.length >= 3) {
      const escaped = catalogSearchString.replace(/'/g, "''");
      return `&$filter=startswith(Project/Name,'${encodeURIComponent(escaped)}')`;
    }
    return "";
  }, [isCatalogSearchActive, catalogSearchString]);

  // --- Fetch catalog projects ---
  const fetchCatalogProjects = useCallback(async (signal?: AbortSignal) => {
    if (!iTwinId) { return; }

    const filterStr = buildCatalogFilter();
    const url =
      `${ConfigManager.projectProviderUrl}/odata/Subscriptions?iTwinId=${iTwinId}` +
      `&$count=true&$expand=Project($select=Id,Name,UniqueName,IsOdata)` +
      `&$select=ProjectId&$top=50&$orderby=Project/Name asc${filterStr}`;
    const runId = crypto.randomUUID();
    setIsCatalogProjectsLoading(true);
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessTokenRef.current,
          RunId: runId,
        },
        signal,
      });
      if (!response.ok) {
        setErrorState(response.status, dispatch, runId, false);
        return;
      }
      const data = await response.json();
      if (data && data.value) {
        const projectsDtos: CatalogGeneralInfo[] =
          data.value.map((sub: any) => mapDataToModels<CatalogGeneralInfo>([sub.Project])[0]).filter(Boolean);
        setCatalogProjects(projectsDtos);
        setCatalogTotalCount(data["@odata.count"] ?? projectsDtos.length);
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        setErrorState(500, dispatch, runId, true);
      }
    } finally {
      setIsCatalogProjectsLoading(false);
    }
  }, [dispatch, iTwinId, buildCatalogFilter]);

  // --- Clear state when iTwinId changes ---
  useEffect(() => {
    setCatalogProjects([]);
    setSelectedCatalogProject(null);
    setCatalogTotalCount(0);
    setCatalogRawInput("");
    setCatalogSearchString(undefined);
    setIsCatalogSearchActive(false);
  }, [iTwinId]);

  // --- Auto-fetch catalog projects (debounced) ---
  // Skip fetch when user typed 1-2 chars (results are cleared, hint is shown).
  const shouldFetchCatalog = catalogRawInput.trim().length === 0 || catalogRawInput.trim().length >= 3;
  useDebouncedFetch(iTwinId && shouldFetchCatalog ? fetchCatalogProjects : null, 300);

  // --- Fetch more catalog projects (pagination via $skip) ---
  const fetchMoreAbortRef = useRef<AbortController | null>(null);
  const fetchMoreCatalogProjects = useCallback(async () => {
    if (catalogProjects.length >= catalogTotalCount) { return; }
    fetchMoreAbortRef.current?.abort();
    fetchMoreAbortRef.current = new AbortController();
    setIsCatalogProjectsLoading(true);
    const runId = crypto.randomUUID();

    const filterStr = buildCatalogFilter();
    const url =
      `${ConfigManager.projectProviderUrl}/odata/Subscriptions?iTwinId=${iTwinId}` +
      `&$count=true&$expand=Project($select=Id,Name,UniqueName,IsOdata)` +
      `&$select=ProjectId&$top=50&$skip=${catalogProjects.length}&$orderby=Project/Name asc${filterStr}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessTokenRef.current,
          RunId: runId,
        },
        signal: fetchMoreAbortRef.current.signal,
      });
      if (!response.ok) {
        setErrorState(response.status, dispatch, runId, false);
        return;
      }
      const data = await response.json();
      if (data?.value) {
        const projectsDtos: CatalogGeneralInfo[] =
          data.value.map((sub: any) => mapDataToModels<CatalogGeneralInfo>([sub.Project])[0]).filter(Boolean);
        setCatalogProjects((prev) => {
          const existingIds = new Set(prev.map((p) => p.Id));
          const newUnique = projectsDtos.filter((p) => !existingIds.has(p.Id));
          return [...prev, ...newUnique];
        });
        setCatalogTotalCount(data["@odata.count"] ?? catalogTotalCount);
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        setErrorState(500, dispatch, runId, true);
      }
    } finally {
      setIsCatalogProjectsLoading(false);
    }
  }, [catalogProjects.length, catalogTotalCount, dispatch, iTwinId, buildCatalogFilter]);

  // When ComboBox selects an item it auto-fills the input with the label,
  // which fires inputProps.onChange.  We skip that event so the search state
  // stays unchanged and the dropdown keeps showing the full result list.
  const justSelectedRef = useRef(false);

  // --- Catalog ComboBox input handler ---
  const catalogComboboxInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      if (justSelectedRef.current) {
        justSelectedRef.current = false;
        return;
      }
      const val = event.currentTarget.value;
      setCatalogRawInput(val);
      const trimmed = val.trim();
      if (trimmed === "") {
        setCatalogSearchString(undefined);
        setIsCatalogSearchActive(false);
      } else if (trimmed.length >= 3) {
        setCatalogSearchString(trimmed);
        setIsCatalogSearchActive(true);
        // Show loading immediately so "No projects found" doesn't flash
        // during the debounce delay.
        setIsCatalogProjectsLoading(true);
      } else {
        // 1-2 chars: clear results, show hint
        setCatalogSearchString(undefined);
        setIsCatalogSearchActive(false);
        setCatalogProjects([]);
        setCatalogTotalCount(0);
      }
    },
    [],
  );

  // Selection handler
  const handleCatalogProjectSelect = useCallback((project: CatalogGeneralInfo | null) => {
    setSelectedCatalogProject(project);
    justSelectedRef.current = true;
  }, []);

  // --- Fetch project details and populate InputExample ---
  const projectDetailAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!selectedCatalogProject || !accessTokenRef.current) {
      return;
    }

    if (projectDetailAbortRef.current) {
      projectDetailAbortRef.current.abort();
    }
    projectDetailAbortRef.current = new AbortController();

    const runId = crypto.randomUUID();

    void ProjectApiHelper.fetchProjectData({
      projId: selectedCatalogProject.Id,
      accessToken: accessTokenRef.current,
      dispatch,
      runId,
      signal: projectDetailAbortRef.current.signal,
    }).then(({ projectDto }) => {
      if (projectDto?.InputInfo?.InputExample) {
        setRequestBody(projectDto.InputInfo.InputExample);
      } else {
        setRequestBody("{}");
      }
      setRequestBodyKey((prev) => prev + 1);
    });

    return () => {
      if (projectDetailAbortRef.current) {
        projectDetailAbortRef.current.abort();
      }
    };
  }, [selectedCatalogProject, dispatch]);

  // --- Select options ---
  const catalogProjectSelectOptions = useMemo(() => {
    return catalogProjects.map((project): SelectOption<CatalogGeneralInfo> => ({
      value: project,
      label: project.Name,
    }));
  }, [catalogProjects]);

  // --- Empty content ---
  const catalogEmptyContent = useMemo(() => {
    if (isCatalogProjectsLoading) { return loadingSkeleton(6); }
    return searchEmptyMessage(catalogRawInput, 3, "No projects found");
  }, [isCatalogProjectsLoading, catalogRawInput]);

  return {
    catalogProjects,
    selectedCatalogProject,
    handleCatalogProjectSelect,
    isCatalogProjectsLoading,
    catalogComboboxInputChange,
    catalogProjectSelectOptions,
    catalogEmptyContent,
    catalogTotalCount,
    fetchMoreCatalogProjects,
    requestBody,
    setRequestBody,
    requestBodyKey,
  };
};
